import { useState, useEffect } from 'react';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

export const useSidebarDnD = ({
    localMoveGroups,
    localApps,
    selectedGroup,
    selectedApp,
    onDragAssignment,
    onVMDragOver,
    updateMoveGroupsApplicationsOrder,
    isDraggingVM,
    draggingVMRecord,
}) => {
    const [dragOverId, setDragOverId] = useState(null);
    const [activeDragItem, setActiveDragItem] = useState(null);
    const [dragHoverGroupId, setDragHoverGroupId] = useState(null);
    const [dragDisabledId, setDragDisabledId] = useState(null);
    const [dragOverAddButton, setDragOverAddButton] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragStart = (event) => {
        document.body.classList.add('rm-dragging');
        setDragHoverGroupId(null);
        setDragDisabledId(null);
        setActiveDragItem(event.active);
    };

    // Highlights move group cards when an app card is dragged over them (within sidebar DndContext only)
    const handleSidebarDragOver = (event) => {
        const { active, over } = event;
        const isDraggingApp = active.data?.current?.type === 'application' && !active.data?.current?.reorderOnly;
        const isOverMoveGroup = over?.data?.current?.type === 'moveGroup';

        if (isDraggingApp && isOverMoveGroup && over.id !== selectedGroup) {
            const sourceGroupId = active.data.current.sourceGroupId;
            const sourceAppId = active.data.current.sourceId;
            // Already in group if: nested-app drag back to same group, OR apps-section drag whose move_group_ids include this group
            const isAlreadyInGroup =
                sourceGroupId != null
                    ? String(sourceGroupId) === String(over.id)
                    : localApps
                          .find((a) => String(a.id) === String(sourceAppId))
                          ?.move_group_ids?.some((mgId) => String(mgId) === String(over.id));

            if (isAlreadyInGroup) {
                setDragOverId(null);
                setDragDisabledId(over.id);
            } else {
                setDragOverId(over.id);
                setDragDisabledId(null);
            }
        } else {
            setDragOverId(null);
            setDragDisabledId(null);
            setDragHoverGroupId(null);
        }
    };

    // Tracks cursor position during a VM drag to highlight sidebar cards;
    // uses native pointermove because VM drags live in a separate DndContext from the sidebar
    useEffect(() => {
        if (!isDraggingVM) {
            setDragOverId(null);
            setDragDisabledId(null);
            setDragHoverGroupId(null);
            setDragOverAddButton(null);
            onVMDragOver?.(null);
            return;
        }
        const handlePointerMove = (e) => {
            const elements = document.elementsFromPoint(e.clientX, e.clientY);

            // Check if pointer is over a + Group or + App add button
            const addBtnEl = elements.find((el) => el.dataset?.dragAddBtn);
            if (addBtnEl) {
                const btnType = addBtnEl.dataset.dragAddBtn;
                setDragOverId(null);
                setDragDisabledId(null);
                setDragHoverGroupId(null);
                setDragOverAddButton(btnType);
                onVMDragOver?.({ type: btnType === 'move-group' ? 'create-move-group' : 'create-application' });
                return;
            }
            setDragOverAddButton(null);

            const cardEl = elements.find((el) => el.dataset?.dragCardId);
            if (cardEl) {
                const rawId = cardEl.dataset.dragCardId;
                const matchingGroup = localMoveGroups.find((g) => String(g.id) === rawId);
                if (matchingGroup) {
                    if (matchingGroup.id === selectedGroup) {
                        setDragOverId(null);
                        onVMDragOver?.(null);
                        return;
                    }
                    if (
                        draggingVMRecord?.move_group_id != null &&
                        String(draggingVMRecord.move_group_id) === String(matchingGroup.id)
                    ) {
                        setDragOverId(null);
                        setDragDisabledId(matchingGroup.id);
                        setDragHoverGroupId(null);
                        onVMDragOver?.(null);
                        return;
                    }
                    setDragOverId(matchingGroup.id);
                    setDragDisabledId(null);
                    setDragHoverGroupId(matchingGroup.id);
                    onVMDragOver?.({ id: matchingGroup.id, type: 'moveGroup' });
                    return;
                }
                const allApps = [...localApps, ...localMoveGroups.flatMap((g) => g.applications || [])];
                const matchingApp = allApps.find((a) => String(a.id) === rawId);
                if (matchingApp) {
                    if (matchingApp.id === selectedApp) {
                        setDragOverId(null);
                        onVMDragOver?.(null);
                        return;
                    }
                    if (
                        draggingVMRecord?.application_id != null &&
                        String(draggingVMRecord.application_id) === String(matchingApp.id)
                    ) {
                        setDragOverId(null);
                        setDragDisabledId(matchingApp.id);
                        const isNestedDisabled = elements.some((el) => el.dataset?.groupZoneId);
                        if (isNestedDisabled) {
                            const parentGroup = localMoveGroups.find((g) =>
                                g.applications?.some((a) => a.id === matchingApp.id),
                            );
                            setDragHoverGroupId(parentGroup ? parentGroup.id : null);
                        } else {
                            setDragHoverGroupId(null);
                        }
                        onVMDragOver?.(null);
                        return;
                    }
                    setDragOverId(matchingApp.id);
                    setDragDisabledId(null);
                    // Only expand parent move group if the card is actually nested (data-group-zone-id in element stack),
                    // not if the same app ID appears in the standalone Apps section
                    const isNestedCard = elements.some((el) => el.dataset?.groupZoneId);
                    if (isNestedCard) {
                        const parentGroup = localMoveGroups.find((g) =>
                            g.applications?.some((a) => a.id === matchingApp.id),
                        );
                        setDragHoverGroupId(parentGroup ? parentGroup.id : null);
                    } else {
                        setDragHoverGroupId(null);
                    }
                    onVMDragOver?.({ id: matchingApp.id, type: 'application' });
                    return;
                }
            }
            // Cursor is in a move group's sortable zone (gap or nested-apps container) but not over a specific card
            const zoneEl = elements.find((el) => el.dataset?.groupZoneId);
            if (zoneEl) {
                const rawGroupId = zoneEl.dataset.groupZoneId;
                const zoneGroup = localMoveGroups.find((g) => String(g.id) === rawGroupId);
                if (zoneGroup) {
                    if (
                        draggingVMRecord?.move_group_id != null &&
                        String(draggingVMRecord.move_group_id) === String(zoneGroup.id)
                    ) {
                        setDragOverId(null);
                        setDragDisabledId(zoneGroup.id);
                        setDragHoverGroupId(null);
                        onVMDragOver?.(null);
                        return;
                    }
                    setDragOverId(zoneGroup.id);
                    setDragDisabledId(null);
                    setDragHoverGroupId(zoneGroup.id);
                    onVMDragOver?.({ id: zoneGroup.id, type: 'moveGroup' });
                    return;
                }
            }
            setDragOverId(null);
            setDragDisabledId(null);
            setDragHoverGroupId(null);
            setDragOverAddButton(null);
            onVMDragOver?.(null);
        };
        document.addEventListener('pointermove', handlePointerMove);
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            setDragOverId(null);
            setDragDisabledId(null);
            setDragHoverGroupId(null);
            setDragOverAddButton(null);
            onVMDragOver?.(null);
        };
    }, [isDraggingVM, localMoveGroups, localApps, selectedGroup, selectedApp, onVMDragOver, draggingVMRecord]);

    // Handles app→moveGroup reassignment and both card-type reorderings within the sidebar DndContext
    const handleUnifiedDragEnd = (event) => {
        const { active, over } = event;
        document.body.classList.remove('rm-dragging');
        setActiveDragItem(null);
        setDragOverId(null);
        setDragDisabledId(null);
        setDragHoverGroupId(null);

        if (!over) return;

        if (active.data?.current?.type === 'vm') {
            if (over.data?.current?.type === 'application') {
                const targetApp = localApps.find((a) => a.id === over.id);
                if (targetApp && onDragAssignment) {
                    onDragAssignment({
                        type: 'vm',
                        sourceId: active.data.current.sourceId,
                        sourceIds: active.data.current.sourceIds,
                        targetType: 'application',
                        targetId: targetApp.id,
                        targetName: targetApp.name,
                    });
                }
                return;
            } else if (over.data?.current?.type === 'moveGroup') {
                const targetGroup = localMoveGroups.find((g) => g.id === over.id);
                if (targetGroup && onDragAssignment) {
                    onDragAssignment({
                        type: 'vm',
                        sourceId: active.data.current.sourceId,
                        sourceIds: active.data.current.sourceIds,
                        targetType: 'moveGroup',
                        targetId: targetGroup.id,
                        targetName: targetGroup.name,
                    });
                }
                return;
            }
        }

        // Resolve app id: nested-app drags use active.data.current.sourceId; app-section drags use active.id
        const activeAppId = active.data?.current?.sourceId ?? active.id;
        const activeApp =
            localApps.find((a) => a.id === activeAppId) ||
            localMoveGroups.flatMap((g) => g.applications || []).find((a) => a.id === activeAppId);
        const overMoveGroup = localMoveGroups.find((g) => g.id === over.id);

        if (
            activeApp &&
            overMoveGroup &&
            over.data?.current?.type === 'moveGroup' &&
            !active.data?.current?.reorderOnly
        ) {
            // Block drop if the app is already assigned to this group
            const sourceGroupId = active.data?.current?.sourceGroupId;
            const isAlreadyInGroup =
                sourceGroupId != null
                    ? String(sourceGroupId) === String(overMoveGroup.id)
                    : localApps
                          .find((a) => String(a.id) === String(activeApp.id))
                          ?.move_group_ids?.some((mgId) => String(mgId) === String(overMoveGroup.id));
            if (isAlreadyInGroup) return;

            if (onDragAssignment) {
                onDragAssignment({
                    type: 'application',
                    sourceId: activeApp.id,
                    targetType: 'moveGroup',
                    targetId: overMoveGroup.id,
                    sourceName: activeApp.name,
                    targetName: overMoveGroup.name,
                });
            }
            return;
        }

        const activeMoveGroup = localMoveGroups.find((g) => g.id === active.id);
        const overGroup = localMoveGroups.find((g) => g.id === over.id);

        if (activeMoveGroup && overGroup && active.id !== over.id) {
            const oldIndex = localMoveGroups.findIndex((item) => item.id === active.id);
            const newIndex = localMoveGroups.findIndex((item) => item.id === over.id);
            const reordered = arrayMove(localMoveGroups, oldIndex, newIndex);
            updateMoveGroupsApplicationsOrder(
                reordered.map((g) => g.id),
                localApps.map((a) => a.id),
            );
            return;
        }

        const activeAppForReorder = localApps.find((a) => a.id === active.id);
        const overApp = localApps.find((a) => a.id === over.id);

        if (activeAppForReorder && overApp && active.id !== over.id) {
            const oldIndex = localApps.findIndex((item) => item.id === active.id);
            const newIndex = localApps.findIndex((item) => item.id === over.id);
            const reordered = arrayMove(localApps, oldIndex, newIndex);
            updateMoveGroupsApplicationsOrder(
                localMoveGroups.map((g) => g.id),
                reordered.map((a) => a.id),
            );
        }
    };

    return {
        dragOverId,
        activeDragItem,
        dragHoverGroupId,
        dragDisabledId,
        dragOverAddButton,
        sensors,
        handleDragStart,
        handleSidebarDragOver,
        handleUnifiedDragEnd,
    };
};
